import React, { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost } from '../lib/api';
import { Badge } from '../lib/badge';
import Icon from '../components/icons';
import { EmptyState } from './ListView';
import { canOperate } from '../lib/roles';
import RoleBanner from '../components/RoleBanner';

const PLANE_UI = 'http://54.167.31.169:8083';
const PLANE_WORKSPACE = 'claude-skills';

function planeBrowseUrl(projectIdentifier, sequenceId) {
  if (!projectIdentifier || !sequenceId) return null;
  return `${PLANE_UI}/${PLANE_WORKSPACE}/browse/${projectIdentifier}-${sequenceId}/`;
}

function skillMeta(skill) {
  const meta = skill?.metadata && typeof skill.metadata === 'object' ? skill.metadata : {};
  return {
    description: String(meta.description || '').trim(),
    examplePrompt: String(meta.example_prompt || '').trim(),
  };
}

function formFromSkill(skill, prev) {
  if (!skill) return prev;
  const { description, examplePrompt } = skillMeta(skill);
  return {
    ...prev,
    skill_key: skill.key,
    title: skill.name || skill.key,
    description: description || examplePrompt || `Run skill ${skill.key}`,
    risk_tier: skill.risk_tier != null ? Number(skill.risk_tier) : prev.risk_tier,
  };
}

function PmBadge({ task }) {
  const project = task.workspace_plane_project_identifier;
  const seq = task.plane_issue_sequence_id;
  const href = planeBrowseUrl(project, seq);
  if (!href) return <span className="cell-muted">—</span>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={`${project}-${seq}`}
      className="pill-link pill-link--plane"
    >
      {project}-{seq}
    </a>
  );
}

export default function RoutingDemo() {
  const [tasks, setTasks] = useState([]);
  const [skills, setSkills] = useState([]);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [form, setForm] = useState({
    workspace_id: 1,
    title: '',
    description: '',
    skill_key: '',
    risk_tier: 1,
  });

  const loadTasks = useCallback(async () => {
    try {
      const data = await apiGet('/tasks');
      setTasks(Array.isArray(data) ? data.slice(0, 10) : []);
    } catch (e) {
      setErr(String(e));
    }
  }, []);

  const loadSkills = useCallback(async () => {
    try {
      const data = await apiGet('/skills');
      const list = (Array.isArray(data) ? data : [])
        .filter((s) => ['enabled', 'reviewed'].includes(String(s.lifecycle || '').toLowerCase()))
        .sort((a, b) => String(a.name || a.key).localeCompare(String(b.name || b.key)));
      setSkills(list);
      setForm((prev) => {
        if (prev.skill_key) {
          const match = list.find((s) => s.key === prev.skill_key);
          return match ? formFromSkill(match, prev) : prev;
        }
        if (list.length) return formFromSkill(list[0], prev);
        return prev;
      });
    } catch (e) {
      setErr(String(e));
    }
  }, []);

  useEffect(() => {
    loadTasks();
    loadSkills();
  }, [loadTasks, loadSkills]);

  function onSkillChange(e) {
    const key = e.target.value;
    const skill = skills.find((s) => s.key === key);
    setForm((prev) => formFromSkill(skill, { ...prev, skill_key: key }));
  }

  async function createTask(e) {
    e.preventDefault();
    setErr('');
    setMsg('');
    if (!form.skill_key) {
      setErr('Pick a skill first.');
      return;
    }
    try {
      const task = await apiPost('/tasks', {
        workspace_id: Number(form.workspace_id),
        customer_id: 1,
        title: form.title,
        description: form.description,
        risk_tier: Number(form.risk_tier),
        routing_mode: 'auto',
        status: 'queued',
        created_by: 'mvp-demo',
      });
      setMsg(`Task #${task.id} created for skill ${form.skill_key}.`);
      loadTasks();
    } catch (e2) {
      setErr(String(e2));
    }
  }

  async function routeTask(task) {
    setErr('');
    setResult(null);
    if (!form.skill_key) {
      setErr('Pick a skill before routing.');
      return;
    }
    const workspace_id = Number(task.workspace_id);
    try {
      const route = await apiPost('/route', {
        workspace_id,
        task_id: task.id,
        skill_key: form.skill_key,
        min_autonomy: 0,
        risk_tier: Number(form.risk_tier),
      });
      const applied = await apiPost('/route/apply', {
        task_id: task.id,
        workspace_id,
        skill_key: form.skill_key,
        min_autonomy: 0,
        risk_tier: Number(form.risk_tier),
      });
      setResult({ route, applied });
      setMsg(`Task #${task.id} routed to agent ${route.agent_name || route.agent_id} (${form.skill_key}). Syncing to Plane…`);
      setTimeout(loadTasks, 2000);
    } catch (e2) {
      setErr(String(e2));
    }
  }

  const selected = skills.find((s) => s.key === form.skill_key);
  const selectedMeta = skillMeta(selected);

  return (
    <main className="page">
      <div className="page__header">
        <span className="page__icon"><Icon name="routing" /></span>
        <div className="page__header-text">
          <h2>Routing Demo</h2>
          <p>Pick a skill, create a task intake, then route — live against the real engine + skill worker.</p>
        </div>
      </div>
      <RoleBanner action="create tasks or trigger routing" />
      {err && <div className="status status--error">{err}</div>}
      {msg && <div className="status status--muted">{msg}</div>}

      {canOperate() && (
        <section className="panel">
          <h3>Create Task Intake</h3>
          <form className="form-grid form-grid--3col" onSubmit={createTask}>
            <label>
              Skill
              <select
                className="field-input"
                value={form.skill_key}
                onChange={onSkillChange}
                required
              >
                {skills.length === 0 && <option value="">No enabled skills</option>}
                {skills.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.name || s.key} ({s.key})
                  </option>
                ))}
              </select>
            </label>
            <label>
              Workspace ID
              <input
                className="field-input"
                type="number"
                min={1}
                value={form.workspace_id}
                onChange={(e) => setForm({ ...form, workspace_id: e.target.value })}
              />
            </label>
            <label>
              Risk tier
              <input
                className="field-input"
                type="number"
                min={0}
                max={3}
                value={form.risk_tier}
                onChange={(e) => setForm({ ...form, risk_tier: e.target.value })}
              />
            </label>
            <label className="form-grid__full">
              Title
              <input
                className="field-input"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
              />
            </label>
            <label className="form-grid__full">
              Description
              <textarea
                className="field-input"
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </label>
            {selected && (
              <p className="panel__footnote form-grid__full">
                Skill key <code>{selected.key}</code>
                {selectedMeta.examplePrompt
                  ? ` · example prompt available (${selectedMeta.examplePrompt.slice(0, 80)}${selectedMeta.examplePrompt.length > 80 ? '…' : ''})`
                  : ''}
              </p>
            )}
            <button type="submit" className="btn-primary form-grid__submit" disabled={!form.skill_key}>
              Create Task
            </button>
          </form>
        </section>
      )}

      <section className="panel">
        <h3>Recent Tasks</h3>
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: '60px' }}>ID</th>
                <th>Title</th>
                <th style={{ width: '110px' }}>Status</th>
                <th style={{ width: '100px' }}>PM (Plane)</th>
                <th style={{ width: '160px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {tasks.length === 0 && (
                <tr><td colSpan={5}><EmptyState label="No tasks yet." /></td></tr>
              )}
              {tasks.map((t) => (
                <tr key={t.id}>
                  <td className="cell-strong">{t.id}</td>
                  <td className="cell-truncate">{t.title}</td>
                  <td><Badge value={t.status} /></td>
                  <td><PmBadge task={t} /></td>
                  <td>
                    {canOperate() ? (
                      <button type="button" className="btn-secondary btn-small" onClick={() => routeTask(t)}>
                        Route &amp; Apply
                      </button>
                    ) : (
                      <span className="cell-muted">View only</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="panel__footnote">
          Route uses the skill selected above. Plane badge = synced to{' '}
          <a href={`${PLANE_UI}/${PLANE_WORKSPACE}/projects/`} target="_blank" rel="noopener noreferrer">Plane CE</a>
          .
        </p>
      </section>

      {result && (
        <section className="panel">
          <h3>Last Routing Result</h3>
          {result.applied?.plane_issue_sequence_id || result.applied?.run ? (
            <div className="callout callout--info">
              <span className="callout__title">Routed</span>
              <span className="callout__meta">
                Workspace {result.applied?.workspace_id || form.workspace_id} · skill {form.skill_key}
                {' '}— Plane sync is fire-and-forget; refresh Tasks shortly.
              </span>
            </div>
          ) : (
            <div className="callout callout--muted">
              Plane sync in progress — reload tasks in a moment.
            </div>
          )}
          <pre className="code-block">{JSON.stringify(result, null, 2)}</pre>
        </section>
      )}
    </main>
  );
}
